import React, { useEffect, useState } from "react";
import mermaid from "mermaid";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      justifyContent: "center",
      margin: theme.spacing(3, 0),
      padding: theme.spacing(3),
      backgroundColor: theme.palette.type === "dark" ? "#1a2332" : "#f8faff",
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      overflow: "auto",
      "& svg": {
        maxWidth: "100%",
        height: "auto"
      }
    },
    fallback: {
      margin: theme.spacing(3, 0),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.type === "dark" ? "#1a2332" : "#f8faff",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      overflow: "auto",
      fontSize: 13
    }
  })
);

mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "inherit",
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: "basis"
  }
});

let mermaidCounter = 0;

interface MermaidDiagramProps {
  chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
  const classes = useStyles();
  const [svg, setSvg] = useState<string>("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    mermaidCounter += 1;
    const id = `mermaid-${mermaidCounter}`;

    const apply = (rendered: string) => {
      if (!cancelled) setSvg(rendered);
    };

    try {
      // mermaid 9 renders synchronously and hands the SVG to a callback; mermaid 10 dropped the
      // callback and returns a promise instead. Support both, so an upgrade does not blank the page.
      const result = mermaid.render(id, chart, apply) as unknown;

      if (result && typeof (result as Promise<unknown>).then === "function") {
        (result as Promise<{ svg: string }>).then(({ svg: rendered }) => apply(rendered)).catch(fail);
      } else if (typeof result === "string") apply(result);

    } catch (err) { fail(err); }

    return () => {
      cancelled = true;
    };

    function fail(err: unknown) {
      // A diagram that does not parse must cost its own block, never the whole page
      // eslint-disable-next-line no-console
      console.warn("Mermaid render error:", err);

      if (!cancelled) setFailed(true);
    }
  }, [chart]);

  // Falling back to the source keeps the information reachable when the diagram cannot be drawn
  if (failed) return <pre className={classes.fallback}>{chart}</pre>;

  if (!svg) return null;

  return (
    <div
      className={classes.container}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidDiagram;
